import * as THREE from 'three';
import { Gait } from './Gait';
import { PostureController } from './Posture';
import { EyeLife } from './Eyes';
import type { Command, Trick } from '../commands';
import { DogSounds } from './Sounds';
import type { DogProfile } from './Profile';
import type { DogBody } from './Body';

/**
 * The dog's behaviour — every MOTION layer, with no meshes of its own:
 * locomotion with turn lag, speed-blended walk/trot/gallop with planted feet,
 * body bob, breathing, weight shift, sit/lie posture states, head look-at
 * with lag, eyes that lead the head + blink + micro-saccades, ear flicks,
 * wagging tail with real spring physics.
 * It drives a `DogBody` (`PrimitiveBody` capsules, or the skinned `RiggedBody`),
 * which can be swapped at runtime without the dog forgetting anything.
 *
 * Convention: the dog faces +Z in its own space. root.rotation.y is heading.
 */

const _v = new THREE.Vector3();
const _push = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _qPrev = new THREE.Quaternion();
const _e = new THREE.Euler(0, 0, 0, 'YXZ');
const _scratch = new THREE.Object3D();
const _up = new THREE.Vector3(0, 1, 0);

function lerpAngle(a: number, b: number, t: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}

export class Dog {
  /** the meshes; swappable (placeholder → rigged) */
  body: DogBody;
  /** ground pivot, heading = rotation.y */
  get root(): THREE.Group {
    return this.body.root;
  }
  /** what she's wearing, so a new body can be dressed the same */
  private hatKind: 'party' | 'santa' | 'witch' | null = null;
  private collarOpts: { color: number; studs: boolean; tag: boolean; tier: number } | null = null;
  private dirt = 0;
  /** smoothed head orientation in the body's neck-parent space */
  private headQ = new THREE.Quaternion();

  private gait = new Gait();
  /** a hind leg is sore: shorter, stiffer stride and slower */
  limping = false;
  private grooming: 'brush' | 'bath' | 'vet' | null = null;
  private shake = 0; // body shake-off, decays
  private roll = 0; // rolling on the back, decays
  private drinking = false;
  /** going home / at home (house) */
  homing = false;
  settledHome = false;
  private homeBed: THREE.Vector3 | null = null;
  /** the world keeps her out of trunks and walls: nudge `pos` and return the nudge (she slides round) */
  keepOut?: (pos: THREE.Vector3, heading: number, out: THREE.Vector3) => THREE.Vector3;
  private slideSide = 1;
  /** hooks for the world: end of a sniff-walk, end of a meal */
  onSniffEnd?: () => void;
  onMealEnd?: () => void;
  /** doing her business: circle (poo only) → squat → kick (wee only) → walk off */
  private business: { kind: 'pee' | 'poop'; t: number; onMark: () => void; onDone: () => void; marked: boolean } | null = null;
  /** exertion 0..1: rises while running, decays at rest; she pants while it's high */
  private exertion = 0;
  private posture = new PostureController();
  private eyeLife = new EyeLife();

  private target: THREE.Vector3 | null = null;
  private speed = 0;
  private t = 0;
  private breath = 0;
  private nextFlick = 2;
  private lookPoint = new THREE.Vector3(0, 1, 5);
  excitement = 0; // 0..1, drives wag
  private eyeYaw = 0;
  private eyePitch = 0;
  /** a heard command, acted on after a short "processing" lag */
  private pending: { cmd: Command; at: number; from: THREE.Vector3 } | null = null;
  /** vocalisations; `sounds.unlock()` must be called from a user gesture once */
  readonly sounds: DogSounds;
  private barkPulse = 0; // head jerk that accompanies a bark, decays fast
  private nextWhine = 30;
  private wasLying = false;
  private commandsHeard = 0;
  private ignoredLast = false;
  private phoneFlat = false;
  private phoneFlatSince = 0;
  private flatHandled = false; // the current flat episode already made her lie down
  /** 0 full .. 1 starving; driven by the feeding loop */
  hunger = 0;
  eating = false;
  private eatPoint = new THREE.Vector3();
  private chew = 0;
  /** a treat in her mouth: chewed for a couple of seconds with hearts, then gone */
  private treat: { t: number; mesh: THREE.Group; hearts: THREE.Mesh[]; crunched: number } | null = null;
  /** a trick in progress */
  private trick: { name: Trick; t: number; dur: number } | null = null;
  /** engaged with a visitor: 'chase' (run + bark) or 'freeze' (locked stare, growl) */
  private engaged: 'chase' | 'freeze' | null = null;
  /** nose-to-ground amble: a short wander with the head low, then a look up */
  private sniffing = false;
  /** desktop walk mode: when you wander off she catches up and settles a couple of metres away */
  heel = false;
  private heelAt = 0;
  private sniffUntil = 0;
  private nextSniff = 12;

  readonly maxSpeed: number;
  /** >1 makes her run at things instead of easing up to them (the agility course) */
  hurry = 1;

  constructor(
    readonly profile: DogProfile,
    body: DogBody,
  ) {
    this.sounds = new DogSounds(profile.sounds);
    this.maxSpeed = 3.4 * profile.size;
    this.body = body;
  }

  /** Swap the meshes under her: position, heading, what's in her mouth and what she wears carry over. */
  setBody(body: DogBody): void {
    const old = this.body;
    if (body === old) return;
    body.root.position.copy(old.root.position);
    body.root.rotation.copy(old.root.rotation);
    body.root.userData = old.root.userData;
    const held = old.mouth.children[0];
    if (held) {
      old.mouth.remove(held);
      body.mouth.add(held);
    }
    old.dispose();
    this.body = body;
    this.headQ.identity();
    this.setHat(this.hatKind);
    if (this.collarOpts) this.setCollar(this.collarOpts);
    this.setCoat(this.dirt);
  }

  /** Walk to a point on the ground. */
  /** @param quiet re-targeting (e.g. chasing a moving toy): no bark, no excitement reset */
  goTo(p: THREE.Vector3, quiet = false): void {
    if (this.engaged && !quiet) return; // not now
    if (!quiet) {
      this.homing = false;
      this.settledHome = false;
      this.homeBed = null;
    }
    this.eating = false;
    this.sniffing = false;
    this.target = p.clone().setY(0);
    const r = this.target.length();
    if (r > 85) this.target.multiplyScalar(85 / r); // the land's edge
    this.lookPoint.copy(this.target).setY(0.15);
    // even a quiet request means getting up; quiet only skips the bark and the excitement reset
    if (this.posture.state !== 'stand') this.posture.wake();
    if (quiet) return;
    this.excitement = 1;
    this.posture.wake();
    // a far dash gets an excited bark or two as she launches
    const dist = this.target.distanceTo(this.root.position);
    if (dist > 2.5 && Math.random() < 0.6) this.bark(Math.random() < 0.4 ? 2 : 1, 1, 0.25);
  }

  /** world position of her nose */
  nosePosition(out: THREE.Vector3): THREE.Vector3 {
    return this.body.mouth.getWorldPosition(out);
  }

  lookAt(p: THREE.Vector3): void {
    this.lookPoint.copy(p);
  }

  /** hold something in her mouth (null to drop). The object is re-parented to the mouth. */
  carry(obj: THREE.Object3D | null): void {
    const mouth = this.body.mouth;
    const held = mouth.children[0];
    if (held) {
      mouth.remove(held);
      held.scale.setScalar(1);
      this.root.parent?.add(held);
    }
    if (obj) {
      mouth.add(obj);
      obj.position.set(0, 0, 0.05);
      obj.rotation.set(0, 0, 0);
      obj.scale.setScalar(1 / this.profile.size);
    }
  }

  /**
   * She got a treat: it stays in her mouth while she chews (three crunches over ~2 s), the
   * tail goes flat out, and a few hearts float up. Then it's gone.
   */
  enjoyTreat(): void {
    if (this.treat) this.endTreat();
    const bone = new THREE.Group();
    const mat = new THREE.MeshStandardMaterial({ color: 0xe9d9b8, roughness: 0.8 });
    const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.014, 0.07, 8), mat);
    bar.rotation.z = Math.PI / 2;
    bone.add(bar);
    for (const x of [-0.035, 0.035]) {
      for (const y of [-0.012, 0.012]) {
        const k = new THREE.Mesh(new THREE.SphereGeometry(0.016, 8, 6), mat);
        k.position.set(x, y, 0);
        bone.add(k);
      }
    }
    bone.position.set(0, -0.01, 0.06);
    this.body.mouth.add(bone);
    this.treat = { t: 0, mesh: bone, hearts: this.makeHearts(5), crunched: 0 };
    this.chew = 1;
    this.excitement = 1;
    this.lookPoint.copy(this.root.position).add(_v2.set(0, 0.6, 1.2).applyAxisAngle(_up, this.root.rotation.y));
    _v2.set(0, 5, 0);
    this.body.tail.kick(_v2);
  }

  private makeHearts(n: number): THREE.Mesh[] {
    const hearts: THREE.Mesh[] = [];
    const heartMat = new THREE.MeshBasicMaterial({ color: 0xe0527a, transparent: true, opacity: 0.9 });
    for (let i = 0; i < n; i++) {
      const h = new THREE.Mesh(new THREE.SphereGeometry(0.025, 8, 6), heartMat.clone());
      h.scale.set(1.2, 1, 0.6);
      h.visible = false;
      h.userData.at = 0.25 + i * 0.35;
      h.userData.dx = (Math.random() - 0.5) * 0.3;
      this.root.add(h);
      hearts.push(h);
    }
    return hearts;
  }

  /** hearts without a treat: praise, a belly rub, "I love you" */
  private praiseHearts: { t: number; hearts: THREE.Mesh[] } | null = null;
  praise(from: THREE.Vector3): void {
    if (this.praiseHearts) for (const h of this.praiseHearts.hearts) h.removeFromParent();
    this.praiseHearts = { t: 0, hearts: this.makeHearts(6) };
    this.excitement = 1;
    this.lookPoint.copy(from);
    _v2.set(0, 5, 0);
    this.body.tail.kick(_v2);
    if (Math.random() < 0.6) this.bark(1, 0.6, 0.2);
  }

  /** come and sit close, looking up at you */
  private cuddle = false;
  comeSit(you: THREE.Vector3): void {
    _v.copy(this.root.position).sub(you).setY(0);
    const d = _v.length();
    if (d > 1.3) {
      _v.multiplyScalar(1.1 / d).add(you).setY(0);
      this.goTo(_v, true);
      this.cuddle = true;
    } else {
      this.target = null;
      this.posture.request('sit', 20);
    }
    this.lookPoint.copy(you);
    this.praise(you);
  }

  private endTreat(): void {
    if (!this.treat) return;
    this.treat.mesh.removeFromParent();
    for (const h of this.treat.hearts) h.removeFromParent();
    this.treat = null;
  }

  /** being petted: where the hand is (world), how long for, which side it's on */
  private petPoint: THREE.Vector3 | null = null;
  private petT = 0;
  private petSide = 1;
  private petHuffed = false;
  private nextPetHearts = 0;
  get petted(): boolean {
    return this.petPoint !== null;
  }
  petStart(p: THREE.Vector3): void {
    this.petPoint = p.clone();
    this.petT = 0;
    this.petHuffed = false;
    this.nextPetHearts = 1.2;
    this.target = null;
    this.sniffing = false;
    this.pending = null;
    this.excitement = 1;
  }
  petMove(p: THREE.Vector3 | null): void {
    if (!this.petPoint) return;
    if (p) this.petPoint.copy(p);
  }
  petEnd(): void {
    if (!this.petPoint) return;
    this.petPoint = null;
    this.excitement = 1;
    _v2.set(0, 4, 0);
    this.body.tail.kick(_v2);
  }

  /** a howl: sit, nose to the sky, and the recording if she has one */
  howl(): void {
    this.target = null;
    this.sniffing = false;
    this.pending = null;
    if (this.posture.state === 'lie') this.posture.request('sit', 20);
    this.lookPoint.copy(this.root.position).add(_v2.set(0, 6, 1.5).applyAxisAngle(_up, this.root.rotation.y));
    this.excitement = 0.3;
    if (!this.sounds.play('howl', 0.8)) this.sounds.whine();
    this.barkPulse = 0.4;
  }

  /** a jump: all four feet off the ground for a moment (frisbee catch) */
  private hopT = 1;
  leap(): void {
    this.hopT = 0;
    this.excitement = 1;
    _v2.set(0, 6, -2).applyAxisAngle(_up, this.root.rotation.y);
    for (const e of this.body.ears) e.kick(_v2);
  }

  /** reaching down to something on the ground: front end crouches, nose goes to it */
  private reach: { p: THREE.Vector3; until: number } | null = null;
  private crouch = 0;
  reachDown(p: THREE.Vector3, seconds = 0.7): void {
    this.reach = { p: p.clone(), until: this.t + seconds };
    this.target = null;
  }

  /** quick head dip when grabbing something off the ground */
  grab(): void {
    this.chew = 1;
    this.lookPoint.copy(this.root.position).setY(0.02);
    window.setTimeout(() => this.lookPoint.copy(this.target ?? this.lookPoint), 350);
  }

  private bark(count: number, intensity: number, delay = 0): void {
    this.sounds.bark(count, intensity, this.profile.barkPitch, delay);
    // the body barks too: head snaps up, tail gets a kick
    window.setTimeout(() => (this.barkPulse = 1), delay * 1000);
  }

  /** the Tricks module hooks in here: return true if it handled the command */
  onTrick?: (name: Trick, from: THREE.Vector3) => boolean;

  /**
   * React to a spoken command. The reaction is immediate (ears prick, head
   * turns to the speaker); compliance comes a beat later, and now and then
   * not at all — a dog, not a remote control.
   */
  hear(cmd: Command, from: THREE.Vector3): void {
    if (this.engaged) return; // a squirrel outranks you
    this.homing = false;
    this.settledHome = false;
    this.homeBed = null;
    this.eating = false;
    this.sniffing = false;
    this.lookPoint.copy(from);
    this.excitement = Math.min(1, this.excitement + 0.5);
    _v2.set(0, 4, -3).applyAxisAngle(_up, this.root.rotation.y);
    for (const e of this.body.ears) e.kick(_v2);
    if (cmd === 'name') {
      if (Math.random() < 0.3) this.bark(1, 0.7, 0.3);
      return; // just attention
    }
    if (cmd === 'spin' || cmd === 'shake' || cmd === 'rollover' || cmd === 'speak' || cmd === 'playdead') {
      this.onTrick?.(cmd, from);
      return;
    }
    // she may ignore you now and then, but never your first few commands and never twice running
    this.commandsHeard++;
    const obeys = this.commandsHeard <= 3 || this.ignoredLast || Math.random() < 0.9;
    this.ignoredLast = !obeys;
    this.pending = obeys ? { cmd, at: this.t + 0.35 + Math.random() * 0.6, from: from.clone() } : null;
    if (!obeys && Math.random() < 0.5) this.sounds.whine(0.5);
  }

  get time(): number {
    return this.t;
  }
  get moving(): boolean {
    return this.target !== null || this.speed > 0.05;
  }
  /** standing around with nothing to do (not walking, not eating, not mid-command) */
  get idle(): boolean {
    return !this.moving && !this.eating && this.pending === null && this.body.mouth.children.length === 0 && this.engaged === null && this.trick === null && this.grooming === null && this.roll < 0.1 && this.business === null && !this.homing;
  }

  /** walk to the door, then in to the bed, and lie down */
  goHome(door: THREE.Vector3, bed: THREE.Vector3): void {
    this.homing = true;
    this.settledHome = false;
    this.homeBed = bed.clone();
    this.sniffing = false;
    this.goTo(door, true);
    this.excitement = 0;
  }

  leaveHome(door: THREE.Vector3): void {
    this.homing = false;
    this.settledHome = false;
    this.homeBed = null;
    this.posture.request('stand', 10);
    this.goTo(door, true);
    this.excitement = 0.4;
  }

  doBusiness(kind: 'pee' | 'poop', onMark: () => void, onDone: () => void): void {
    this.target = null;
    this.sniffing = false;
    this.business = { kind, t: 0, onMark, onDone, marked: false };
    this.lookPoint.copy(this.root.position).add(_v2.set(0, 0.1, 1).applyAxisAngle(_up, this.root.rotation.y));
    if (kind === 'pee') this.posture.request('sit', 2.2);
  }

  /** how far into a sit she is, 0..1 (props that follow her back need it) */
  get sitting(): number {
    return this.posture.sit;
  }

  get sniffingNow(): boolean {
    return this.sniffing;
  }

  /** a holiday hat on top of her head, or none */
  setHat(kind: 'party' | 'santa' | 'witch' | null): void {
    this.hatKind = kind;
    const hat = this.body.hat;
    hat.clear();
    if (!kind) return;
    const col = kind === 'party' ? 0x7b5cc4 : kind === 'santa' ? 0xd0342c : 0x2a2230;
    const cone = new THREE.Mesh(new THREE.ConeGeometry(kind === 'witch' ? 0.11 : 0.075, kind === 'witch' ? 0.24 : 0.2, 14), new THREE.MeshStandardMaterial({ color: col, roughness: 0.7 }));
    cone.position.y = (kind === 'witch' ? 0.24 : 0.2) / 2;
    cone.castShadow = true;
    hat.add(cone);
    if (kind === 'party') {
      const pom = new THREE.Mesh(new THREE.SphereGeometry(0.025, 8, 6), new THREE.MeshStandardMaterial({ color: 0xf2c94c }));
      pom.position.y = 0.2;
      hat.add(pom);
    } else if (kind === 'santa') {
      const white = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1 });
      const rim = new THREE.Mesh(new THREE.TorusGeometry(0.078, 0.022, 8, 20), white);
      rim.rotation.x = Math.PI / 2;
      const pom = new THREE.Mesh(new THREE.SphereGeometry(0.03, 8, 6), white);
      pom.position.set(0.05, 0.19, -0.03);
      hat.add(rim, pom);
    } else {
      const brim = new THREE.Mesh(new THREE.CylinderGeometry(0.19, 0.19, 0.012, 20), new THREE.MeshStandardMaterial({ color: col, roughness: 0.7 }));
      hat.add(brim);
    }
    hat.rotation.z = 0.12;
  }

  /** dress her: a ring, optional studs, optional hanging tag. tier 0 = none */
  setCollar(opts: { color: number; studs: boolean; tag: boolean; tier: number }): void {
    this.collarOpts = opts;
    const collar = this.body.collar;
    collar.clear();
    // The placeholder has no neck (head sits on the body), so it shows none; the rigged body does.
    if (opts.tier === 0 || !this.body.showsCollar) return;
    const r = this.body.collarRadius;
    const band = new THREE.MeshStandardMaterial({ color: opts.color, roughness: 0.55, metalness: opts.tier >= 4 ? 0.6 : 0 });
    const ring = new THREE.Mesh(new THREE.TorusGeometry(r, 0.024, 8, 32), band);
    ring.castShadow = true;
    collar.add(ring);
    if (opts.studs) {
      const stud = new THREE.MeshStandardMaterial({ color: 0xe8e8e8, roughness: 0.25, metalness: 0.8 });
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2;
        const m = new THREE.Mesh(new THREE.SphereGeometry(0.012, 8, 6), stud);
        m.position.set(Math.cos(a) * r, Math.sin(a) * r, 0.02);
        collar.add(m);
      }
    }
    if (opts.tag) {
      const tag = new THREE.Mesh(
        new THREE.CylinderGeometry(0.022, 0.022, 0.006, 12),
        new THREE.MeshStandardMaterial({ color: 0xd9c27a, roughness: 0.3, metalness: 0.7 }),
      );
      // hangs from the bottom of the ring
      tag.position.set(0, -r - 0.03, 0.02);
      tag.rotation.x = Math.PI / 2;
      collar.add(tag);
    }
  }

  /** 0 clean .. 1 filthy: dulls and muddies the coat */
  setCoat(dirt: number): void {
    this.dirt = dirt;
    this.body.setCoat(dirt, this.wet);
  }

  /** 0 dry .. 1 soaked: darker, glossier coat that dries off over a minute or so */
  private wet = 0;
  soak(amount: number): void {
    this.wet = Math.min(1, Math.max(this.wet, amount));
    this.body.setCoat(this.dirt, this.wet);
  }

  /** brush/bath/vet ritual: she sits, eyes half close, tail slow */
  groom(on: boolean, kind: 'brush' | 'bath' | 'vet'): void {
    this.grooming = on ? kind : null;
    if (on) {
      this.target = null;
      this.eating = false;
      this.sniffing = false;
      this.pending = null;
      this.posture.request(kind === 'vet' ? 'stand' : 'sit', 30);
    } else {
      this.posture.request('stand', 10);
      this.excitement = 1;
    }
  }

  /** the wet-dog shake */
  shakeOff(): void {
    this.shake = 1;
    this.sounds.play('shake', 0.7, 0.1);
    _v2.set(0, 6, 0);
    for (const e of this.body.ears) e.kick(_v2);
    this.body.tail.kick(_v2);
  }

  /** flop and wriggle on her back in something wonderful */
  rollInIt(): void {
    this.target = null;
    this.sniffing = false;
    this.roll = 1;
    this.posture.request('lie', 4);
    window.setTimeout(() => this.posture.request('stand', 8), 3200);
    window.setTimeout(() => this.shakeOff(), 3600);
  }

  sip(): void {
    this.chew = 1;
  }

  get trickActive(): boolean {
    return this.trick !== null;
  }

  /** do a trick (the Tricks module decides whether she will) */
  perform(name: Trick, you: THREE.Vector3): void {
    this.target = null;
    this.eating = false;
    this.sniffing = false;
    this.pending = null;
    this.lookPoint.copy(you);
    this.excitement = 1;
    const dur = { spin: 1.1, shake: 1.6, rollover: 1.8, speak: 0.8, playdead: 3.2 }[name];
    this.trick = { name, t: 0, dur };
    if (name === 'shake') this.posture.request('sit', 8);
    if (name === 'rollover' || name === 'playdead') this.posture.request('lie', name === 'playdead' ? 3.5 : 3);
    if (name === 'speak') this.bark(2, 1, 0.15);
  }

  /** didn't get it: head tilt (an ear flick and a sideways look), no move */
  confused(you: THREE.Vector3): void {
    this.lookPoint.copy(you);
    this.pending = null;
    _v2.set(3, 2, 0).applyAxisAngle(_up, this.root.rotation.y);
    this.body.ears[Math.random() < 0.5 ? 0 : 1].kick(_v2);
    this.headTilt = Math.random() < 0.5 ? 0.35 : -0.35;
  }
  private headTilt = 0;

  get busy(): boolean {
    return this.engaged !== null;
  }

  /** a visitor has her full attention: drop everything */
  engage(at: THREE.Vector3, mode: 'chase' | 'freeze'): void {
    this.engaged = mode;
    this.eating = false;
    this.sniffing = false;
    this.pending = null;
    this.target = null;
    this.lookPoint.copy(at);
    _v2.set(0, 4, -3).applyAxisAngle(_up, this.root.rotation.y);
    for (const e of this.body.ears) e.kick(_v2);
    if (this.posture.state !== 'stand') this.posture.request('stand', 30);
    this.excitement = mode === 'chase' ? 1 : 0;
  }

  /** run at a moving point (re-targeted each frame); stops `stopAt` metres short */
  chase(p: THREE.Vector3, stopAt = 0.3): void {
    _v.subVectors(p, this.root.position).setY(0);
    const d = _v.length();
    if (d > stopAt) {
      if (d > stopAt + 0.05) _v.multiplyScalar((d - stopAt) / d);
      _v.add(this.root.position);
      this.target = _v.clone();
      this.lookPoint.copy(p).setY(Math.max(0.1, p.y));
    } else {
      this.target = null;
    }
  }

  /** visitor gone: back to normal */
  release(): void {
    if (!this.engaged) return;
    const was = this.engaged;
    this.engaged = null;
    this.target = null;
    this.excitement = 1;
    if (was === 'freeze') this.bark(1, 0.8, 0.4);
    this.nextSniff = this.t + 6 + Math.random() * 8;
  }

  /** bark right now (visitors) */
  barkNow(count: number): void {
    this.bark(count, 1, 0);
  }

  /** Something interesting appeared: look at it, perk up. */
  notice(p: THREE.Vector3): void {
    this.lookPoint.copy(p);
    this.excitement = Math.min(1, this.excitement + 0.4);
    _v2.set(0, 3, -2).applyAxisAngle(_up, this.root.rotation.y);
    for (const e of this.body.ears) e.kick(_v2);
  }

  /** Hungry at an empty bowl: look at it, then at you, and whine. */
  complain(bowl: THREE.Vector3, you: THREE.Vector3): void {
    this.lookPoint.copy(bowl);
    this.sounds.whine(0.2);
    window.setTimeout(() => this.lookPoint.copy(you), 1400);
    if (this.posture.state !== 'stand') this.posture.request('stand', 10);
  }

  /** Head down into the bowl. `bite()` is called by the feeding loop per mouthful. */
  startEating(bowl: THREE.Vector3, mode: 'eat' | 'drink' = 'eat'): void {
    this.drinking = mode === 'drink';
    this.eating = true;
    this.target = null;
    this.pending = null;
    this.eatPoint.copy(bowl).setY(0.05);
    this.lookPoint.copy(this.eatPoint);
    if (this.posture.state !== 'stand') this.posture.request('stand', 30);
  }

  bite(): void {
    this.chew = 1;
    this.sounds.crunch();
  }

  stopEating(you: THREE.Vector3): void {
    if (this.eating && !this.drinking) this.onMealEnd?.();
    this.eating = false;
    this.lookPoint.copy(you);
    this.excitement = Math.min(1, this.excitement + 0.3);
  }

  /**
   * The phone is her window on you. Laid flat for a couple of seconds she
   * settles down; picked back up she sits up and looks at you.
   */
  setPhoneFlat(flat: boolean): void {
    if (flat === this.phoneFlat) return;
    this.phoneFlat = flat;
    this.phoneFlatSince = this.t;
    if (flat) this.flatHandled = false;
  }

  /** A sudden jolt: startle, look at you, maybe a bark. */
  startle(strength: number, from: THREE.Vector3): void {
    this.lookPoint.copy(from);
    this.excitement = Math.min(1, this.excitement + 0.6);
    this.pending = null;
    _v2.set((Math.random() - 0.5) * 4, 6, -4).applyAxisAngle(_up, this.root.rotation.y);
    for (const e of this.body.ears) e.kick(_v2);
    this.barkPulse = 0.6;
    if (this.posture.state !== 'stand') this.posture.request('stand', 20);
    if (strength > 0.3 && Math.random() < 0.7) this.bark(strength > 0.7 ? 2 : 1, 1, 0.15);
  }

  private obey(cmd: Command, from: THREE.Vector3): void {
    switch (cmd) {
      case 'sit':
        this.target = null;
        this.posture.request('sit');
        break;
      case 'down':
        this.target = null;
        this.posture.request('lie');
        break;
      case 'stand':
        this.target = null;
        this.posture.request('stand');
        break;
      case 'stay':
        this.target = null;
        this.posture.request(this.posture.state, 40);
        break;
      case 'come': {
        // a spot on the ground ~2 m in front of the speaker, on the line to the dog
        _v.copy(this.root.position).sub(from).setY(0);
        const d = _v.length();
        if (d < 2.2) break; // already here
        _v.multiplyScalar(2.0 / d).add(from).setY(0);
        this.goTo(_v);
        break;
      }
      case 'name':
      case 'spin':
      case 'shake':
      case 'rollover':
      case 'speak':
      case 'playdead':
        break;
    }
  }

  update(dt: number, cameraPos: THREE.Vector3): void {
    this.t += dt;

    if (this.pending && this.t >= this.pending.at) {
      const p = this.pending;
      this.pending = null;
      this.obey(p.cmd, p.from);
    }

    // --- locomotion: turn toward target with lag, accelerate, decelerate on arrival
    let desiredSpeed = 0;
    if (this.target) {
      _v.subVectors(this.target, this.root.position).setY(0);
      const dist = _v.length();
      if (dist < 0.12) {
        this.target = null;
        this.lookPoint.copy(cameraPos);
      } else {
        const desiredYaw = Math.atan2(_v.x, _v.z);
        this.root.rotation.y = lerpAngle(this.root.rotation.y, desiredYaw, 1 - Math.exp(-5 * dt));
        // slow down while turning sharply, and when close
        const facing = Math.cos(desiredYaw - this.root.rotation.y);
        desiredSpeed = Math.min(this.maxSpeed, dist * 1.6 * this.hurry) * Math.max(0.15, facing);
      }
    }
    // can't run while sat down: locomotion waits for the body to come up
    const up = this.posture.upright;
    desiredSpeed *= up * up;
    // sniffing is an amble, never a trot; frozen means frozen
    if (this.sniffing) desiredSpeed = Math.min(desiredSpeed, 0.45 * this.profile.size);
    if (this.engaged === 'freeze') desiredSpeed = 0;
    if (this.limping) desiredSpeed = Math.min(desiredSpeed, 1.1 * this.profile.size);
    if (this.grooming || this.petPoint) desiredSpeed = 0;
    this.speed += (desiredSpeed - this.speed) * (1 - Math.exp(-4 * dt));
    _v.set(0, 0, 1).applyAxisAngle(_up, this.root.rotation.y);
    this.root.position.addScaledVector(_v, this.speed * dt);
    if (this.speed > 0.01 && this.keepOut) {
      const push = this.keepOut(this.root.position, this.root.rotation.y, _push);
      const back = -push.dot(_v); // how much of the nudge was straight back along her heading
      if (back > 1e-4) {
        // blocked head-on: step sideways by as much as she was pushed back, so she slides round
        const lateral = push.x * _v.z - push.z * _v.x;
        if (Math.abs(lateral) > 1e-4) this.slideSide = Math.sign(lateral);
        this.root.position.x += _v.z * back * this.slideSide;
        this.root.position.z -= _v.x * back * this.slideSide;
      }
    }

    // --- posture state machine (idle → sit → lie), gait generator
    this.posture.update(dt, this.moving || this.eating);
    const sit = this.posture.sit;
    const lie = this.posture.lie;

    // --- phone attitude (edge-triggered, once per episode, so commands still win):
    // laid flat for 2 s → lie down; picked up after that → sit up and look at you
    if (this.phoneFlat && !this.flatHandled && this.t - this.phoneFlatSince > 2) {
      this.flatHandled = true;
      if (this.target === null && this.posture.state !== 'lie') this.posture.request('lie', 60);
    } else if (!this.phoneFlat && this.flatHandled && this.t - this.phoneFlatSince < 0.5) {
      this.flatHandled = false;
      if (this.posture.state === 'lie') this.posture.request('sit');
      this.lookPoint.copy(cameraPos);
    }

    // --- going home: door, then bed, then lie down and stay
    if (this.homing && !this.target && this.homeBed) {
      if (!this.settledHome) {
        if (this.root.position.distanceTo(this.homeBed) > 0.2) {
          this.goTo(this.homeBed, true);
        } else {
          this.settledHome = true;
          this.posture.request('lie', 3600);
          this.lookPoint.copy(this.root.position).add(_v2.set(0, 0.2, 1).applyAxisAngle(_up, this.root.rotation.y));
        }
      }
    }

    // --- sniff-walk: when standing around, wander a short way with the nose down, then look up
    if (this.sniffing) {
      this.sounds.sniff();
      if (this.target) {
        // nose just ahead of the front feet, on the ground
        _v.set(0, 0, 0.75).applyAxisAngle(_up, this.root.rotation.y).multiplyScalar(this.profile.size);
        this.lookPoint.copy(this.root.position).add(_v).setY(0.02);
      } else if (this.t > this.sniffUntil) {
        this.sniffing = false;
        this.lookPoint.copy(cameraPos);
        this.nextSniff = this.t + 10 + Math.random() * 20;
        this.onSniffEnd?.();
      }
    } else if (
      this.idle &&
      this.posture.state === 'stand' &&
      this.t > this.nextSniff &&
      this.hunger < 0.9
    ) {
      _v.set((Math.random() - 0.5) * 2, 0, (Math.random() - 0.5) * 2).setLength(0.5 + Math.random() * 1.0);
      _v.add(this.root.position);
      if (_v.length() > 5) _v.setLength(5);
      this.goTo(_v);
      this.excitement = 0; // no wag, this is business
      this.sniffing = true;
      this.sniffUntil = this.t + 6; // arrival re-arms below
    }
    if (this.sniffing && !this.target && this.sniffUntil > this.t + 3) this.sniffUntil = this.t + 1 + Math.random() * 2;

    // --- heel: you're walking (desktop); if you get more than ~3 m away she comes to about 1.6 m
    if (this.heel && this.idle && !this.sniffing && !this.settledHome && this.t > this.heelAt) {
      _v.copy(cameraPos).setY(0).sub(this.root.position);
      const d = _v.length();
      if (d > 3.2) {
        this.heelAt = this.t + 0.6;
        _v.multiplyScalar((d - 1.6) / d).add(this.root.position);
        this.goTo(_v, true);
      }
    }

    // --- panting: builds up with fast running (and heat), fades over ~20 s at rest
    const running = this.speed > 1.6 * this.profile.size ? 1 : 0;
    this.exertion += ((running ? 1 : 0) - this.exertion) * (1 - Math.exp(-(running ? 0.22 : 0.16) * dt));
    // only after a real run, quietly, and not every breath
    if (this.exertion > 0.7 && !this.eating && !this.trick) this.sounds.pant(0.12 + 0.1 * this.exertion);

    // --- vocal life: a huff as she settles down, the odd whine when left lying
    const lying = this.posture.state === 'lie';
    if (lying && !this.wasLying && Math.random() < 0.7) this.sounds.huff(0.4);
    this.wasLying = lying;
    if (lying && this.t > this.nextWhine) {
      if (Math.random() < 0.5) this.sounds.whine();
      this.nextWhine = this.t + 20 + Math.random() * 25;
    } else if (!lying) this.nextWhine = this.t + 15 + Math.random() * 15;
    this.sounds.setDistance(this.root.position.distanceTo(cameraPos));
    if (this.treat) {
      const tr = this.treat;
      tr.t += dt;
      this.excitement = 1;
      // three crunches, the bone shrinking with each
      const due = Math.floor(tr.t / 0.6);
      if (due > tr.crunched && due <= 3) {
        tr.crunched = due;
        this.chew = 1;
        this.sounds.crunch();
        tr.mesh.scale.setScalar(1 - 0.3 * due);
      }
      for (const h of tr.hearts) {
        const age = tr.t - h.userData.at;
        h.visible = age > 0 && age < 1.4;
        if (h.visible) {
          h.position.set(h.userData.dx + Math.sin(age * 5) * 0.04, 0.85 + age * 0.45, 0.55);
          (h.material as THREE.MeshBasicMaterial).opacity = 0.9 * (1 - age / 1.4);
        }
      }
      if (tr.t > 2.4) this.endTreat();
    }
    if (this.praiseHearts) {
      const ph = this.praiseHearts;
      ph.t += dt;
      for (const h of ph.hearts) {
        const age = ph.t - h.userData.at;
        h.visible = age > 0 && age < 1.4;
        if (h.visible) {
          h.position.set(h.userData.dx + Math.sin(age * 5) * 0.04, 0.85 + age * 0.45, 0.55);
          (h.material as THREE.MeshBasicMaterial).opacity = 0.9 * (1 - age / 1.4);
        }
      }
      if (ph.t > 2.8) {
        for (const h of ph.hearts) h.removeFromParent();
        this.praiseHearts = null;
      }
    }
    if (this.cuddle && !this.target) {
      this.cuddle = false;
      this.posture.request('sit', 20);
      this.lookPoint.copy(cameraPos);
    }
    if (this.wet > 0) {
      // drying: slow while sitting, faster after a shake; below a whisker it's dry
      this.wet *= Math.exp(-(this.shake > 0.05 ? 0.6 : 0.02) * dt);
      if (this.wet < 0.01) this.wet = 0;
      this.body.setCoat(this.dirt, this.wet);
    }
    this.barkPulse *= Math.exp(-14 * dt);
    // the gait works in the dog's own metres; the body moves in world metres
    this.gait.update(dt, this.speed / this.profile.size);

    // --- legs: the gait's feet (planted / arcing); the body blends them with its sit / lie poses
    if (this.limping) {
      // sore back-right leg: short, guarded stride, foot kept off the ground a little
      const f = this.gait.feet[3];
      f.z *= 0.35;
      f.lift = Math.max(f.lift * 0.5, 0.035);
    }
    let shakeLift = 0;
    // --- being petted: she leans into the hand, half-closes her eyes, wags; a long scratch gets the hind-leg thump
    let scratchKick = 0;
    if (this.petPoint) {
      this.petT += dt;
      this.excitement = 1;
      this.lookPoint.copy(this.petPoint).y += 0.15;
      _v2.subVectors(this.petPoint, this.root.position);
      _v.set(1, 0, 0).applyAxisAngle(_up, this.root.rotation.y);
      this.petSide = _v2.dot(_v) >= 0 ? 1 : -1;
      if (!this.petHuffed && this.petT > 0.7) {
        this.petHuffed = true;
        this.sounds.huff(0);
      }
      if (this.petT > this.nextPetHearts) {
        this.nextPetHearts = this.petT + 2.5;
        this.praise(this.petPoint);
      }
      // the thump: after a couple of seconds the near hind leg goes
      if (this.petT > 1.6) scratchKick = Math.sin(this.t * 16) * this.petSide * Math.min(1, (this.petT - 1.6) * 2);
    }

    // --- business: poo = circle 2.5 s then squat; wee = squat 2.2 s then two grass kicks
    let kick = 0;
    if (this.business) {
      const b = this.business;
      b.t += dt;
      if (b.kind === 'poop') {
        if (b.t < 2.5) {
          this.root.rotation.y += ((Math.PI * 2 * 2.5) / 2.5) * dt * 0.6; // slow circling
          this.lookPoint.copy(this.root.position).add(_v2.set(0, 0.02, 0.5).applyAxisAngle(_up, this.root.rotation.y));
        } else if (b.t < 2.6) {
          this.posture.request('sit', 2.4);
        }
        if (b.t > 4.3 && !b.marked) {
          b.marked = true;
          b.onMark();
        }
        if (b.t > 5.0) {
          this.business = null;
          this.posture.request('stand', 10);
          _v2.set(0, 0, 1.0).applyAxisAngle(_up, this.root.rotation.y).add(this.root.position);
          this.goTo(_v2, true);
          b.onDone();
        }
      } else {
        if (b.t > 1.0 && !b.marked) {
          b.marked = true;
          b.onMark();
        }
        if (b.t > 2.2 && b.t < 3.0) {
          // kick: hind legs flick back alternately
          const u = (b.t - 2.2) / 0.8;
          kick = Math.sin(u * Math.PI * 2);
        }
        if (b.t > 3.0) {
          this.business = null;
          this.posture.request('stand', 10);
          b.onDone();
        }
      }
    }

    // --- tricks: layered on top of posture
    let rollAngle = 0;
    if (this.trick) {
      const tr = this.trick;
      tr.t += dt;
      const u = Math.min(1, tr.t / tr.dur);
      switch (tr.name) {
        case 'spin':
          // one full turn in place, easing out
          this.root.rotation.y += (Math.PI * 2 * dt) / tr.dur;
          break;
        case 'shake':
          // sit, then lift the right fore leg forward for most of the trick
          shakeLift = Math.sin(Math.PI * u);
          break;
        case 'rollover':
          // once she's down, roll a full turn about her long axis
          rollAngle = Math.PI * 2 * THREE.MathUtils.smoothstep(u, 0.25, 0.95);
          break;
        case 'playdead':
          // flop onto her side and stay there, legs stiff, until the end
          rollAngle = (Math.PI / 2) * THREE.MathUtils.smoothstep(u, 0.15, 0.4) * (1 - THREE.MathUtils.smoothstep(u, 0.85, 1));
          break;
        case 'speak':
          break;
      }
      if (u >= 1) {
        this.trick = null;
        if (tr.name === 'rollover' || tr.name === 'playdead') this.posture.request('stand', 12);
        this.lookPoint.copy(cameraPos);
      }
    }
    this.headTilt *= Math.exp(-2.5 * dt);
    // shake-off: fast side-to-side wobble that dies out; roll: on her back and wriggling
    this.shake *= Math.exp(-2.2 * dt);
    this.roll *= Math.exp(-0.45 * dt);
    if (this.shake > 0.02) rollAngle += Math.sin(this.t * 40) * 0.35 * this.shake;
    if (this.roll > 0.1) rollAngle += Math.PI * Math.min(1, this.roll * 1.2) + Math.sin(this.t * 6) * 0.25 * this.roll;

    const body = this.body;
    body.legs(this.gait.feet, sit, lie, shakeLift, kick || scratchKick);

    // --- reaching down: crouch the front end and put the nose on the thing
    if (this.reach && this.t > this.reach.until) this.reach = null;
    this.crouch += ((this.reach ? 1 : 0) - this.crouch) * (1 - Math.exp(-9 * dt));
    if (this.reach) this.lookPoint.copy(this.reach.p);

    // --- torso: bob + gait pitch + accel pitch, blended with sit / lie (nose-down pitch is positive)
    body.torso(this.gait.bob - 0.07 * this.crouch, -this.gait.pitch - (desiredSpeed - this.speed) * 0.05 + 0.32 * this.crouch, sit, lie);
    // rolling over / playing dead: rotate the whole body about its long axis. The root pivot is on
    // the ground, so lift it to keep the body's centre (≈0.22 m up when lying) at the same height.
    this.root.rotation.z = rollAngle;
    this.root.position.y = rollAngle ? 0.22 * (1 - Math.cos(rollAngle)) : 0;
    if (this.hopT < 1) {
      this.hopT = Math.min(1, this.hopT + dt / 0.45);
      this.root.position.y += 0.32 * Math.sin(Math.PI * this.hopT);
    }

    // --- idle life: breathing (slower, deeper when lying), weight shift when standing
    const idle = 1 - THREE.MathUtils.smoothstep(this.speed, 0.05, 0.5);
    // breathing rate: ~18/min resting, ~13/min lying, up to ~210/min panting after a run
    const restHz = 0.3 - lie * 0.08;
    const breathHz = restHz + (3.5 - restHz) * Math.pow(this.exertion, 0.8);
    this.breath += dt * breathHz * Math.PI * 2;
    // panting is fast and shallow; a sleeping dog is slow and deep
    const breathAmp = idle * (1 + lie * 0.8) * (1 - 0.6 * this.exertion) + this.exertion * 0.35;
    body.breathe(Math.sin(this.breath) * breathAmp);
    if (!rollAngle) this.root.rotation.z = Math.sin(this.t * 0.45) * 0.012 * idle * (1 - sit) * (this.engaged === 'freeze' ? 0 : 1) + (this.petPoint ? -0.05 * this.petSide * Math.min(1, this.petT * 2) : 0);

    // --- head look-at with lag and joint limits (in the neck's parent space, since it pitches)
    body.neckParent.worldToLocal(_v.copy(this.lookPoint));
    _scratch.position.copy(body.neckPos);
    _scratch.lookAt(_v);
    _e.setFromQuaternion(_scratch.quaternion, 'YXZ');
    _e.y = THREE.MathUtils.clamp(_e.y, -1.2, 1.2);
    this.chew *= Math.exp(-3 * dt);
    const chewBob = this.eating ? Math.sin(this.t * (this.drinking ? 22 : 14)) * (this.drinking ? 0.03 : 0.05) * this.chew : 0;
    const noseDown = this.eating ? 1.15 : this.sniffing ? 1.0 : 0.6 + 0.7 * this.crouch;
    _e.x = THREE.MathUtils.clamp(_e.x, -0.5, noseDown) - this.barkPulse * 0.35 + chewBob; // bark: nose jerks up
    _e.z = this.headTilt;
    _q.setFromEuler(_e);
    _qPrev.copy(this.headQ);
    // a bark is a snap, not an ease: the pulse also speeds the head up
    this.headQ.slerp(_q, 1 - Math.exp(-(4 + this.barkPulse * 30) * dt));
    body.look(this.headQ);
    const headAngSpeed = _qPrev.angleTo(this.headQ) / Math.max(dt, 1e-4);

    // --- eyes: lead the head (residual angle to the target in head space),
    // plus micro-saccades and blinks
    this.eyeLife.update(dt, headAngSpeed);
    _scratch.quaternion.copy(this.headQ).invert();
    _v.sub(body.neckPos).applyQuaternion(_scratch.quaternion); // now in head space
    const eyeYawT = THREE.MathUtils.clamp(Math.atan2(_v.x, _v.z), -0.45, 0.45) + this.eyeLife.saccadeYaw;
    const eyePitchT =
      THREE.MathUtils.clamp(-Math.atan2(_v.y, Math.hypot(_v.x, _v.z)), -0.35, 0.35) + this.eyeLife.saccadePitch;
    const eyeK = 1 - Math.exp(-22 * dt);
    this.eyeYaw += (eyeYawT - this.eyeYaw) * eyeK;
    this.eyePitch += (eyePitchT - this.eyePitch) * eyeK;
    const lid = (0.08 + 0.92 * this.eyeLife.openness) * (this.grooming === 'brush' ? 0.55 : 1) * (this.petPoint ? 0.5 : 1);
    body.eyes(this.eyeYaw, this.eyePitch, lid);

    // --- ear flick on a random timer
    if (this.t > this.nextFlick) {
      const ear = body.ears[Math.random() < 0.5 ? 0 : 1];
      _v2.set((Math.random() - 0.5) * 6, -3, 2).applyAxisAngle(_up, this.root.rotation.y);
      ear.kick(_v2);
      this.nextFlick = this.t + 2 + Math.random() * 5;
    }

    // --- tail wag: lateral force in world space, strongest when excited or arriving
    this.excitement = Math.max(0, this.excitement - dt * 0.15);
    const wag = (0.35 + this.excitement * 0.65) * (1 - lie * 0.6) * (this.grooming ? 0.4 : 1);
    const wagFreq = (6 + this.excitement * 6) * (this.grooming ? 0.5 : 1);
    _v2.set(1, 0, 0).applyAxisAngle(_up, this.root.rotation.y);
    body.tail.force.copy(_v2).multiplyScalar(Math.sin(this.t * wagFreq) * 60 * wag);
    body.tail.force.y += this.barkPulse * 80; // bark: tail flicks up
    if (this.engaged === 'freeze') body.tail.force.y += 45; // hackles: tail stiff and high

    // --- the body last: IK, chains, meshes react to everything above
    body.finish(dt);
  }
}
